import type {
  PreparedSurveyGroup,
  SurveySession,
} from "../../../session/SurveySession";
import {
  groupResponseFromAnswer,
  type SurveyGroupResponse,
} from "../../../session/transport";
import type { TranscriptProjection } from "../../../session/transcript";
import type { Question } from "../../../util/types";
import {
  isQuestionGroupContainerRaw,
  questionGroupChildRaw,
} from "../../questionGroup/core";
import { questionRegistry } from "../../registry";
import type { AttemptDraftState } from "../types";
import { groupDraftScopeKey, groupDraftValue } from "./draftState";
import type { SurveyCopy } from "../../../theme/copy";
import { defaultThemeV2 } from "../../../theme/defaultThemeV2";
import { AUTO_RESPONSE_MSG_CONTENT } from "../../../util/constants";

export const prepareGroupSubmission = (
  question: Question,
  state: AttemptDraftState | null | undefined,
  session: SurveySession,
  copy: SurveyCopy = defaultThemeV2.copy,
): PreparedSurveyGroup | null => {
  const raw = question.question;
  /* v8 ignore next -- the forms group host only supplies group questions */
  if (!isQuestionGroupContainerRaw(raw)) return null;
  const responses: SurveyGroupResponse[] = [];
  const projections: TranscriptProjection[] = [];
  for (const child of raw.children) {
    if (child.answered) continue;
    if (child.question.auto_response === "ok") {
      responses.push(AUTO_RESPONSE_MSG_CONTENT);
      continue;
    }
    const module = questionRegistry.get(child.question.type);
    /* v8 ignore next -- group child modules are checked by supportsSurveyQuestion */
    if (!module) return null;
    const definition = module.core.decode(questionGroupChildRaw(child));
    const draft = groupDraftValue(
      state,
      session,
      groupDraftScopeKey(raw.group_id, question.sequence_id),
      child,
      module,
      definition,
    );
    const validation = module.core.validate(definition, draft, copy);
    /* v8 ignore next -- the Forms submit button requires every pending draft to validate */
    if (!validation.valid) return null;
    responses.push(
      groupResponseFromAnswer(module.core.encode(definition, draft)),
    );
    projections.push(module.core.projectTranscript(definition, draft, copy));
  }
  /* v8 ignore next -- the forms action is disabled when no children are pending */
  return responses.length === 0 ? null : { responses, projections };
};
